<template>
    <div
        class="OrganisationHeader"
        :class="{ withLogo: hasVisibleLogo }"
    >
		<div class="Info">
			<div class="OrganisationInfo">
				<strong
					v-skeleton="{
						loading,
						sizes: {
							Small: { width: '7.5625rem', height: '0.75rem' },
						}
					}"
					class="OrganisationName"
					:title="organisationName"
				>
					{{ organisationName }}
				</strong>
			</div>
			<span
				v-skeleton="{
					loading,
					sizes: {
						Small: { width: '11.563rem', height: '0.75rem' },
					},
					margins: {
						Small: '0.25rem 0',
					},
				}"
				class="Locations"
			>
				{{ mainLocation }} <span v-if="extraLocationsCount">+{{ extraLocationsCount }}</span>
			</span>

			<RatingCount
				:reviewRating="reviewRating"
				:loading="loading"
				:clickable="true"
				@click.stop="reviewClick"
			/>
		</div>

        <OrganisationLogo
			v-if="hasVisibleLogo"
			class="Logo"
			:imageUrl="optimizedOrganisationUrl"
			:organisationName="organisationName"
			:loading="loading"
		/>
    </div>
</template>


<script setup lang="ts">
import { computed, PropType } from 'vue';
import OrganisationLogo from '@components/SearchResults/Card/OrganisationLogo/OrganisationLogo.vue';
import { IReviewRating } from '@app/interfaces/dtos/IReviewRating';

const props = defineProps({
	organisationName: {
		type: String,
		default: '',
	},
	organisationLogoUrl: {
		type: String,
		default: '',
	},
	organisationUrl: {
		type: String,
		default: '',
	},
	programmeId: {
		type: String,
		default: '',
	},
	ranking: {
		type: String,
		required: false,
	},
	showLogo: {
		type: Boolean,
		default: true,
	},
	locations: {
		type: Array as () => string[],
		required: false,
	},
	loading: {
		type: Boolean,
		default: false,
	},
	reviewRating: {
		type: Object as PropType<IReviewRating | undefined>,
		default: undefined,
	},
});


const hasVisibleLogo = computed(() => props.showLogo || props.loading);
const optimizedOrganisationUrl = computed(() => {
	if (!props.organisationLogoUrl) {
		return '';
	}

	return `${props.organisationLogoUrl}?auto=format&fit=fill&fill=solid&fill-color=00FFFFFF&w=40&h=40&q=70`;
});

const mainLocation = computed(() => props.locations && props.locations.length > 0 ? props.locations[0] : '');
const extraLocationsCount = computed(() => props.locations && props.locations.length > 1 ? props.locations.length - 1 : 0);
const reviewClick = () => {
	setTimeout(() => {
		window.open(`${props.organisationUrl}?ref=search_card&openreviewsdrawer=true`, isMobile.value ? '_self' : '_blank');
	}, 0);
}
</script>

<style lang="scss" scoped>
	.OrganisationHeader {
		width: 100%;
		display: flex;
		justify-content: space-between;
		align-items: flex-start;

		.Info {
			display: flex;
			flex-direction: column;
			align-items: flex-start;
			min-width: 0;

			.OrganisationInfo {
				display: flex;
				gap: 0.25rem;
				width: 100%;
				@include MarginAreas(0);

				.OrganisationName {
					white-space: nowrap;
					overflow: hidden;
					text-overflow: ellipsis;
					color: $Grey-900;

					@include TextStyle(HeadingSmall );
					@include MarginAreas(0);
					@include PaddingAreas(0);
				}
			}

			.Locations {
				color: $Grey-900;
				@include TextStyle(UiSmallRegular);
				@include MarginAreas(0.5, 0);

				span {
					color: $Grey-700;
					@include TextStyle(UiSmallRegular);

				}
			}
		}

		.Logo {
			flex-shrink: 0;

			@include MarginAreas(0, 0, 0, 0.5);
		}
	}
</style>